import { updateMatterAssignees } from "@/lib/data/requests/matters/matter-assignee";
import { retrieveMatterV3 } from "@/lib/data/requests/matters/matter";
import { updateMatterTeamConnections } from "@/lib/data/requests/matters/matter-team";
import { queryKeys } from "@/lib/data/static/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export function useUpdateMatterPersonnel() {
  const queryClient = useQueryClient();
  const updateTeams = useMutation({ mutationFn: updateMatterTeamConnections });
  const updateAssignees = useMutation({ mutationFn: updateMatterAssignees });

  const refreshMatter = async (matterId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matterId],
      }),
      queryClient.invalidateQueries({ queryKey: [queryKeys.matters] }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterDashboardRows],
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matterDashboardUnassignedActions],
      }),
    ]);
  };

  const getCurrentAssignments = async (matterId: string) => {
    const response = await retrieveMatterV3(matterId);
    return response.data.assignees
      .filter((assignee) => !assignee.dateTimeEnd)
      .map((assignee) => ({
        userId: assignee.user.id,
        roleId: assignee.role.id,
        primaryContact: assignee.primaryContact,
      }));
  };

  const getCurrentTeamIds = async (matterId: string) => {
    const response = await retrieveMatterV3(matterId);
    return [
      ...new Set(
        response.data.teamConnections
          .filter((connection) => !connection.dateTimeEnd)
          .map((connection) => connection.teamId)
          .filter((id): id is string => Boolean(id)),
      ),
    ];
  };

  return {
    isPending: updateTeams.isPending || updateAssignees.isPending,
    updateTeams: updateTeams.mutateAsync,
    updateAssignees: updateAssignees.mutateAsync,
    getCurrentAssignments,
    getCurrentTeamIds,
    refreshMatter,
  };
}
